import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { adminAuth } = useOutletContext();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`);
      if (res.ok) setForm(await res.json());
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) toast.success('Settings saved');
      else toast.error('Failed to save');
    } catch {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Branding, contact, social links & itinerary defaults (used in PDFs)</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Company & Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">Company Name</label>
            <Input value={form.company_name} onChange={(e) => setField('company_name', e.target.value)} /></div>
          <div><label className="block text-sm font-medium mb-1">Logo URL</label>
            <Input value={form.logo_url} onChange={(e) => setField('logo_url', e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Phone</label>
              <Input value={form.phone} onChange={(e) => setField('phone', e.target.value)} /></div>
            <div><label className="block text-sm font-medium mb-1">Email</label>
              <Input value={form.email} onChange={(e) => setField('email', e.target.value)} /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Address</label>
            <Textarea rows={2} value={form.address} onChange={(e) => setField('address', e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Social Links (clickable in PDF footer)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-1">Instagram URL</label>
            <Input data-testid="settings-instagram" value={form.instagram} onChange={(e) => setField('instagram', e.target.value)} placeholder="https://instagram.com/..." /></div>
          <div><label className="block text-sm font-medium mb-1">Facebook URL</label>
            <Input data-testid="settings-facebook" value={form.facebook} onChange={(e) => setField('facebook', e.target.value)} placeholder="https://facebook.com/..." /></div>
          <div><label className="block text-sm font-medium mb-1">YouTube URL</label>
            <Input data-testid="settings-youtube" value={form.youtube} onChange={(e) => setField('youtube', e.target.value)} placeholder="https://youtube.com/@..." /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Itinerary Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Default Tax / Fee Label</label>
              <Input value={form.tax_label} onChange={(e) => setField('tax_label', e.target.value)} placeholder="GST" /></div>
            <div><label className="block text-sm font-medium mb-1">Default Tax / Fee %</label>
              <Input type="number" min="0" step="0.5" value={form.default_tax_percent} onChange={(e) => setField('default_tax_percent', parseFloat(e.target.value) || 0)} /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Default Terms &amp; Conditions</label>
            <Textarea data-testid="settings-terms" rows={10} value={form.default_terms} onChange={(e) => setField('default_terms', e.target.value)} placeholder="One condition per line..." /></div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AdminSettings;
